import { TranslateService } from '@ngx-translate/core';
import { DeviceAndBrowserDetectorService } from 'src/app/services/device-and-browser-detector.service';
import { DeviceErrorType, DeviceState } from 'src/app/models/device-manger';
import { DeviceType } from '../devices-handle-util';
import {
  Browser,
  DeviceErrorHelperModelBase,
  DeviceErrorHelperStep,
} from './device-error-helper-model-base';

/** Opens System Settings → Privacy & Security on macOS (Ventura+). */
const MACOS_SYSTEM_SETTINGS_PRIVACY_SECURITY_URL =
  'x-apple.systempreferences:com.apple.Settings.PrivacySecurity.extension';

export class PermissionHelper extends DeviceErrorHelperModelBase {
  private _steps: DeviceErrorHelperStep[] = [];
  protected helperSupportedBrowsers: string[] = [
    Browser.CHROME,
    Browser.EDGE,
    Browser.SAFARI,
    Browser.FIREFOX,
  ];

  constructor(
    protected deviceDetector: DeviceAndBrowserDetectorService,
    protected translateService: TranslateService,
    protected deviceType: DeviceType,
  ) {
    super(translateService, deviceDetector, deviceType);
  }

  getSteps(): DeviceErrorHelperStep[] {
    this._steps = [];
    this.buildHelperFlow();
    return this._steps;
  }

  getMobileTitle(): string {
    return this.allowBrowserCameraMicTitle();
  }

  getDesktopTitle(): string {
    return this.allowBrowserCameraMicTitle();
  }

  /** Named-browser permission title, or null to use each layout’s generic fallback. */
  private allowBrowserCameraMicTitle(): string {
    const browser = this.BrowserDisplayName();
    return this.translateService.instant('Allow {{browser}} to access camera / mic', { browser });
  }

  hasRelatedError(cam: DeviceState, mic: DeviceState) {
    return (
      this.hasErrorOfType(cam, DeviceErrorType.PERMISSION_DENIED) ||
      this.hasErrorOfType(mic, DeviceErrorType.PERMISSION_DENIED)
    );
  }

  showRefreshButton(): boolean {
    return false;
  }

  /**
   * Chrome / Edge / Firefox: Mac, iPad, iPhone. Safari: iPad & iPhone only (not Mac desktop).
   */
  protected showRealDeviceSettingsButtonByGuide(): boolean {
    const d = this.deviceDetector;
    const onMac = d.isMacOS();
    const onIpad = d.isiOSiPad();
    const onIosPhone = d.isiOSiPhone();
    if (!onMac && !onIpad && !onIosPhone) {
      return false;
    }

    if (d.isChrome() || d.isEdge() || d.isFirefox()) {
      return true;
    }
    if (d.isSafari()) {
      return onIpad || onIosPhone;
    }
    return false;
  }

  deviceSettingButtonSrc(): string {
    if (!this.showRealDeviceSettingsButtonByGuide()) {
      return '';
    }
    if (this.deviceDetector.isMacOS()) {
      return MACOS_SYSTEM_SETTINGS_PRIVACY_SECURITY_URL;
    }
    return '';
  }

  getErrorMsg(cam: DeviceState, mic: DeviceState): string {
    const camError = this.hasErrorOfType(cam, DeviceErrorType.PERMISSION_DENIED);
    const micError = this.hasErrorOfType(mic, DeviceErrorType.PERMISSION_DENIED);

    if (camError && micError) {
      return this.translateService.instant('Access not yet granted');
    } else if (camError) {
      return this.translateService.instant('Cam access not yet granted');
    } else if (micError) {
      return this.translateService.instant('Mic access not yet granted');
    } else {
      return '';
    }
  }

  private buildHelperFlow() {
    if (this.deviceDetector.isAndroidPhone() || this.deviceDetector.isAndroidTablet()) {
      this.buildAndroidHelperFlow();
      return;
    }
    if (this.deviceDetector.isDesktop() && !this.deviceDetector.isTabletDevice()) {
      if (this.deviceDetector.isMacOS()) {
        this.buildMacOSSteps();
        return;
      }
      this.buildNotMacOSDesktopSteps();
      return;
    }
    if (this.deviceDetector.isiOSiPhone() || this.deviceDetector.isiOSiPad()) {
      this.buildiOSHelperFlow();
      return;
    }
  }

  private buildNotMacOSDesktopSteps() {
    this._steps = [];

    if (this.deviceDetector.isEdge()) {
      this.buildNotMacOSDesktopEdgeSteps();
      return;
    }
    if (this.deviceDetector.isChrome()) {
      this.buildNotMacOSDesktopChromeSteps();
      return;
    }
    if (this.deviceDetector.isFirefox()) {
      this.buildNotMacOSDesktopfirefoxSteps();
      return;
    }
    // Non supported browsers, we will use Chrome helper steps
    this.buildNotMacOSDesktopChromeSteps();
  }

  private buildMacOSSteps() {
    this._steps = [];
    if (this.deviceDetector.isSafari()) {
      this.buildMacOSStepsForSafari();
      return;
    }

    this.buildMacOSStepsForChromiumBrowsers();
  }

  private buildiOSHelperFlow() {
    if (this.deviceDetector.isSafari()) {
      this.constructHelperStepsSafariiOS();
      return;
    }
    // Handle the rest supported browsers
    // If the current browser isn't supported yet, images will be the same as chrome
    this.constructHelperStepsOtherBrowsersiOS();
  }

  private buildNotMacOSDesktopEdgeSteps() {
    this._steps.push(
      {
        stepText: `${this.translateService.instant('Click the')} ${this.strongHtml(
          this.translateService.instant('Site permissions'),
        )} ${this.translateService.instant('icon in the address bar')}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-browser/windows/common/step-1.png',
      },
      {
        stepText: `${this.translateService.instant('Allow')} ${this.strongHtml(
          this.translateService.instant('Camera'),
        )} ${this.translateService.instant('and')} ${this.strongHtml(
          this.translateService.instant('Microphone'),
        )}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-browser/windows/edge/step-2.png',
      },
    );
  }

  private buildMacOSStepsForSafari() {
    this._steps.push(
      {
        stepText:
          `${this.translateService.instant('Click')} ${this.strongHtml('Safari')} ` +
          `${this.translateService.instant('in the menu bar and go to')} ` +
          `${this.strongHtml(this.translateService.instant('Settings for This Website...'))}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-browser/macOS/safari/step-1.png',
      },
      {
        stepText:
          `${this.translateService.instant('Select')} ${this.strongHtml(
            this.translateService.instant('Allow'),
          )} ` + `${this.translateService.instant('from the dropdown menu')}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-browser/macOS/safari/step-2.png',
      },
    );
  }

  private buildNotMacOSDesktopfirefoxSteps() {
    this._steps.push(
      {
        stepText: `${this.translateService.instant('Click the')} ${this.strongHtml(
          this.translateService.instant('Camera'),
        )} ${this.translateService.instant('or')} ${this.strongHtml(
          this.translateService.instant('Microphone'),
        )} ${this.translateService.instant('icon in the address bar')}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-browser/windows/firefox/step-1.png',
      },
      {
        stepText: `${this.translateService.instant('Click the')} ${this.strongHtml(
          "'x'",
        )} ${this.translateService.instant('next to')} ${this.strongHtml(
          this.translateService.instant('Blocked Temporarily'),
        )}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-browser/windows/firefox/step-2.png',
      },
    );
  }

  private buildNotMacOSDesktopChromeSteps() {
    this._steps.push(
      {
        stepText: `${this.translateService.instant('Click the')} ${this.strongHtml(
          this.translateService.instant('Site permissions'),
        )} ${this.translateService.instant('icon in the address bar')}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-browser/windows/common/step-1.png',
      },
      {
        stepText: `${this.translateService.instant('Enable')} ${this.strongHtml(
          this.translateService.instant('Camera'),
        )} ${this.translateService.instant('and')} ${this.strongHtml(
          this.translateService.instant('Microphone'),
        )}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-browser/windows/chrome/step-2.png',
      },
    );
  }

  /** Shared macOS steps for Chrome, Edge, and Firefox (same screenshots; copy uses browser name). */
  private buildMacOSStepsForChromiumBrowsers() {
    const browserName = this.BrowserDisplayName();
    const browserNameLowerCase = browserName.toLowerCase();
    this._steps.push(
      {
        stepText:
          `${this.translateService.instant('Open')} ${this.strongHtml(
            this.translateService.instant('System Settings'),
          )} &gt; ` +
          `${this.externalHelpLinkHtml(
            MACOS_SYSTEM_SETTINGS_PRIVACY_SECURITY_URL,
            this.translateService.instant('Privacy & Security'),
          )}`,
      },
      {
        stepText: `${this.translateService.instant('Select')} ${this.strongHtml(
          this.translateService.instant('Microphone'),
        )} ${this.translateService.instant('or')} ${this.strongHtml(
          this.translateService.instant('Camera'),
        )}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-browser/macOS/common/step-2.png',
      },
      {
        stepText:
          `${this.translateService.instant('Find')} ${this.strongHtml(browserName)} ` +
          `${this.translateService.instant('and enable access for both camera and microphone')}`,
        stepImgSrc: `helper-modal-guides/browsers/${browserNameLowerCase}.png`,
      },
    );
  }

  private constructHelperStepsSafariiOS() {
    const firstStep: DeviceErrorHelperStep = {
      stepText: `${this.translateService.instant('Open')} ${this.strongHtml(
        this.translateService.instant('Settings'),
      )} > ${this.strongHtml(this.translateService.instant('Apps'))} > ${this.strongHtml(
        this.translateService.instant('Safari'),
      )}`,
      stepImgSrc: 'helper-modal-guides/blocked-by-browser/iOS/safari/step-1.png',
    };

    this._steps.push(
      firstStep,
      {
        stepText: `${this.translateService.instant('Select')} ${this.strongHtml(
          this.translateService.instant('Camera'),
        )} ${this.translateService.instant('and')} ${this.strongHtml(
          this.translateService.instant('Microphone'),
        )}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-browser/iOS/safari/step-2.png',
      },
      {
        stepText: `${this.translateService.instant('Select')} ${this.strongHtml(
          this.translateService.instant('Allow'),
        )} ${this.translateService.instant('in both sections')}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-browser/iOS/safari/step-3.png',
      },
    );
  }

  private constructHelperStepsOtherBrowsersiOS() {
    const browserName = this.BrowserDisplayName();
    const browserNameLowerCase = browserName.toLowerCase();

    const firstStep: DeviceErrorHelperStep = {
      stepText: `${this.translateService.instant('Open')} ${this.strongHtml(
        this.translateService.instant('Settings'),
      )} > ${this.strongHtml(this.translateService.instant('Apps'))} > ${this.strongHtml(
        browserName,
      )}`,
      stepImgSrc: `helper-modal-guides/blocked-by-browser/iOS/${browserNameLowerCase}/step-1.png`,
    };

    this._steps.push(
      firstStep,
      {
        stepText: `${this.translateService.instant('Enable')} ${this.strongHtml(
          this.translateService.instant('Camera'),
        )} ${this.translateService.instant('and')} ${this.strongHtml(
          this.translateService.instant('Microphone'),
        )}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-browser/iOS/common/step-2.png',
      },
      {
        stepText: `${this.strongHtml(
          this.translateService.instant('Refresh'),
        )} ${this.translateService.instant('Pencil Spaces and click')} ${this.strongHtml(
          this.translateService.instant('Allow'),
        )}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-browser/iOS/common/step-3.png',
      },
    );
  }

  /**
   * Android phone and tablet share the same step text; only images differ (`mobile` vs `tablet`).
   * Browser (Edge → Safari → Chrome → Firefox → default Chrome) selects asset set only.
   * Chrome + mobile: legacy `android_step_{one|two|three}_mobile.png`.
   * Otherwise: `android_{browser}_step_{one|two|three}_{mobile|tablet}.png`.
   */
  private buildAndroidHelperFlow() {
    const browserKey = this.resolveAndroidBrowserKey();
    if (browserKey === 'chrome') {
      this._steps.push(
        {
          stepText:
            `${this.translateService.instant('Tap')} ` +
            `${this.strongHtml(this.translateService.instant('Site permissions'))} ` +
            `${this.translateService.instant('icon in the address bar')}`,
          stepImgSrc: this.androidPermissionStepImage(browserKey, '1'),
        },
        {
          stepText:
            `${this.translateService.instant('Tap')} ` +
            `${this.strongHtml(this.translateService.instant('Permissions'))}`,
          stepImgSrc: this.androidPermissionStepImage(browserKey, '2'),
        },
        {
          stepText:
            `${this.translateService.instant('Enable')} ` +
            `${this.strongHtml(this.translateService.instant('Camera'))} ` +
            `${this.translateService.instant('and')} ` +
            `${this.strongHtml(this.translateService.instant('Microphone'))}`,
          stepImgSrc: this.androidPermissionStepImage(browserKey, '3'),
        },
      );
    } else if (browserKey === 'edge') {
      this._steps.push(
        {
          stepText:
            `${this.translateService.instant('Tap the')} ` +
            `${this.strongHtml(this.translateService.instant('menu'))} ` +
            `${this.translateService.instant('icon in the address bar')}`,
          stepImgSrc: this.androidPermissionStepImage(browserKey, '1'),
        },
        {
          stepText:
            `${this.translateService.instant('Click')} ` +
            `${this.strongHtml(this.translateService.instant('Settings'))} ` +
            'then go to ' +
            `${this.strongHtml(this.translateService.instant('Site settings'))}`,
          stepImgSrc: this.androidPermissionStepImage(browserKey, '2'),
        },
        {
          stepText:
            `${this.translateService.instant('Click')} ` +
            `${this.strongHtml(this.translateService.instant('Site permissions'))} ` +
            `${this.translateService.instant('then select')} ` +
            `${this.strongHtml(this.translateService.instant('Camera'))} ` +
            `${this.translateService.instant('or')} ` +
            `${this.strongHtml(this.translateService.instant('Microphone'))}`,
          stepImgSrc: this.androidPermissionStepImage(browserKey, '3'),
        },
        {
          stepText:
            `${this.translateService.instant('Click')} ` +
            `${this.strongHtml(window.location.origin)} ` +
            `${this.translateService.instant('then choose')} ` +
            `${this.strongHtml(this.translateService.instant('Allow'))} ` +
            `${this.translateService.instant('for both camera and microphone')} `,
          stepImgSrc: this.androidPermissionStepImage(browserKey, '4'),
        },
      );
    } else if (browserKey === 'firefox') {
      this._steps.push(
        {
          stepText:
            `${this.translateService.instant('Tap the')} ` +
            `${this.strongHtml(this.translateService.instant('three-dot'))} ` +
            `${this.translateService.instant('icon in the address bar')}`,
          stepImgSrc: this.androidPermissionStepImage(browserKey, '1'),
        },
        {
          stepText:
            `${this.translateService.instant('Click')} ` +
            `${this.strongHtml(this.translateService.instant('Settings'))} ` +
            '> ' +
            `${this.strongHtml(this.translateService.instant('Site settings'))}`,
          stepImgSrc: this.androidPermissionStepImage(browserKey, '2'),
        },
        {
          stepText:
            `${this.translateService.instant('Select')} ` +
            `${this.strongHtml(this.translateService.instant('Camera'))} ` +
            `${this.translateService.instant('or')} ` +
            `${this.strongHtml(this.translateService.instant('Microphone'))}`,
          stepImgSrc: this.androidPermissionStepImage(browserKey, '3'),
        },
        {
          stepText:
            `${this.translateService.instant('Choose')} ` +
            `${this.strongHtml(this.translateService.instant('Ask to allow'))} ` +
            `${this.translateService.instant('for both camera and microphone')}`,
          stepImgSrc: this.androidPermissionStepImage(browserKey, '4'),
        },
      );
    }
  }

  private resolveAndroidBrowserKey(): 'chrome' | 'edge' | 'safari' | 'firefox' {
    if (this.deviceDetector.isEdge()) {
      return 'edge';
    }
    if (this.deviceDetector.isSafari()) {
      return 'safari';
    }
    if (this.deviceDetector.isChrome()) {
      return 'chrome';
    }
    if (this.deviceDetector.isFirefox()) {
      return 'firefox';
    }
    return 'chrome';
  }

  private androidPermissionStepImage(
    browserKey: 'chrome' | 'edge' | 'safari' | 'firefox',
    step: string,
  ): string {
    return `helper-modal-guides/blocked-by-browser/android/${browserKey}/step-${step}.png`;
  }
}
